import { useState, useEffect, useCallback, useMemo } from 'react'
import { Alert, Box, CircularProgress, Snackbar, Typography } from '@mui/material'
import { BugReport } from '@mui/icons-material'
import { useIssues } from '../hooks/use-issues'
import { PageContainer } from '../components/PageContainer'
import { IssueStatsBar } from '../components/issues/IssueStatsBar'
import { IssueFilterBar } from '../components/issues/IssueFilterBar'
import { IssueTable } from '../components/issues/IssueTable'

const EMPTY_FILTERS = { search: '', status: '', priority: '', customer: '' }

export function AdminIssuesPage() {
	const { issues, loading, error, updateIssue, getAttachmentUrl } = useIssues()
	const [filters, setFilters] = useState(EMPTY_FILTERS)
	const [expandedIssue, setExpandedIssue] = useState(null)
	const [adminNotes, setAdminNotes] = useState({})
	const [savingNote, setSavingNote] = useState(null)
	const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' })

	const showSnackbar = useCallback((message, severity = 'success') => {
		setSnackbar({ open: true, message, severity })
	}, [])

	const stats = useMemo(() => ({
		open: issues.filter((i) => i.status === 'open').length,
		inProgress: issues.filter((i) => i.status === 'in_progress').length,
		resolvedToday: issues.filter((i) => {
			if (i.status !== 'resolved' && i.status !== 'closed') return false
			const d = new Date(i.resolved_at || i.updated_at)
			return d.toDateString() === new Date().toDateString()
		}).length,
		critical: issues.filter((i) => i.priority === 'critical').length,
	}), [issues])

	const customers = useMemo(
		() => [...new Set(issues.map((i) => i.customer?.name).filter(Boolean))].sort(),
		[issues]
	)

	const filteredIssues = useMemo(() => {
		const search = filters.search.toLowerCase()
		return issues.filter(
			(i) =>
				(!search || i.title.toLowerCase().includes(search)) &&
				(!filters.status || i.status === filters.status) &&
				(!filters.priority || i.priority === filters.priority) &&
				(!filters.customer || i.customer?.name === filters.customer)
		)
	}, [issues, filters])

	const handleToggleExpand = useCallback((issueId) => {
		setExpandedIssue((prev) => (prev === issueId ? null : issueId))
	}, [])

	const handleAdminNotesChange = useCallback((issueId, value) => {
		setAdminNotes((prev) => ({ ...prev, [issueId]: value }))
	}, [])

	const handleSaveAdminNotes = useCallback(async (issueId) => {
		const notes = adminNotes[issueId]
		if (notes === undefined) return
		try {
			setSavingNote(issueId)
			await updateIssue(issueId, { admin_notes: notes })
			showSnackbar('Notes saved')
		} catch (err) {
			showSnackbar(err.message || 'Failed to save notes', 'error')
		} finally {
			setSavingNote(null)
		}
	}, [adminNotes, updateIssue, showSnackbar])

	const handleStatusChange = useCallback(async (issueId, newStatus) => {
		try {
			const updates = { status: newStatus }
			if (newStatus === 'resolved' || newStatus === 'closed') {
				updates.resolved_at = new Date().toISOString()
			}
			await updateIssue(issueId, updates)
			showSnackbar('Status updated')
		} catch (err) {
			showSnackbar(err.message || 'Failed to update status', 'error')
		}
	}, [updateIssue, showSnackbar])

	// Seed admin notes when expanding a row
	useEffect(() => {
		if (!expandedIssue) return
		const issue = issues.find((i) => i.id === expandedIssue)
		if (issue && adminNotes[expandedIssue] === undefined) {
			setAdminNotes((prev) => ({ ...prev, [expandedIssue]: issue.admin_notes || '' }))
		}
	}, [expandedIssue, issues, adminNotes])

	if (loading) {
		return (
			<PageContainer>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
					<CircularProgress />
				</Box>
			</PageContainer>
		)
	}

	if (error) {
		return (
			<PageContainer>
				<Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
					<Typography color="error">Error loading issues: {error}</Typography>
				</Box>
			</PageContainer>
		)
	}

	return (
		<PageContainer>
			<Box mb={4}>
				<Typography variant="h4" fontWeight="bold" gutterBottom>
					Issue Tracker
				</Typography>
				<Typography variant="body1" color="text.secondary">
					Triage and resolve customer-reported issues
				</Typography>
			</Box>

			<IssueStatsBar stats={stats} />

			<IssueFilterBar filters={filters} onChange={setFilters} customers={customers} />

			{filteredIssues.length === 0 ? (
				<Box
					display="flex"
					flexDirection="column"
					alignItems="center"
					justifyContent="center"
					py={8}
					gap={1}
				>
					<BugReport sx={{ fontSize: 64, color: 'text.secondary' }} />
					<Typography variant="h6">No issues match your filters</Typography>
					<Typography variant="body2" color="text.secondary">
						{issues.length === 0
							? 'No issues have been reported yet.'
							: 'Try adjusting your filters.'}
					</Typography>
				</Box>
			) : (
				<IssueTable
					issues={filteredIssues}
					expandedIssue={expandedIssue}
					onToggleExpand={handleToggleExpand}
					adminNotes={adminNotes}
					onAdminNotesChange={handleAdminNotesChange}
					onSaveNotes={handleSaveAdminNotes}
					onStatusChange={handleStatusChange}
					getAttachmentUrl={getAttachmentUrl}
					savingNote={savingNote}
				/>
			)}

			<Snackbar
				open={snackbar.open}
				autoHideDuration={4000}
				onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<Alert
					severity={snackbar.severity}
					onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
					variant="filled"
					sx={{ minWidth: 280 }}
				>
					{snackbar.message}
				</Alert>
			</Snackbar>
		</PageContainer>
	)
}
